/**
 * `<CollapsibleCard>` — header + chevron + collapsible body.
 *
 * Foundations spec: design/design-system-foundations.md §9.16.
 * Replaces the prototype's `.eval-collapsible` pattern. Used heavily on
 * the Candidate Page (verdict cards, scorecard wrapper, detailed-
 * assessment wrapper, overview card) — 30j scope.
 *
 * Optional `borderColor` accent applies a tinted-bg + colored-border
 * pair (used on verdict cards). Title color picks up the accent too,
 * matching the prototype's verdict treatment.
 *
 * 30f Phase B8 — additive only. Hand-rolled (not Radix-Collapsible) per
 * architect §7 footnote — gives the accent-tint + bg-tint flexibility
 * the verdict cards need without fighting Radix's primitive contract.
 */
import { useState, type ReactNode } from "react";

interface CollapsibleCardProps {
  title: string;
  /** Defaults: true for verdict cards, false otherwise. */
  defaultOpen?: boolean;
  /** Optional verdict pill / etc rendered next to the title. */
  trailingBadge?: ReactNode;
  /** Optional icon rendered before the title text inside the header button. */
  leadingIcon?: ReactNode;
  /** Optional accent color (CSS color). When set, applies tinted bg + colored border + colored title. */
  borderColor?: string;
  children: ReactNode;
}

export function CollapsibleCard({
  title,
  defaultOpen = false,
  trailingBadge,
  leadingIcon,
  borderColor,
  children,
}: CollapsibleCardProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const wrapperStyle: React.CSSProperties = borderColor
    ? {
        borderColor: `${borderColor}44`, // ~27% alpha
        backgroundColor: `${borderColor}0d`, // ~5% alpha tint
      }
    : {};
  const titleStyle: React.CSSProperties = borderColor
    ? { color: borderColor }
    : {};
  return (
    <div
      data-state={open ? "open" : "closed"}
      className="mb-3 overflow-hidden rounded-xl border border-border-default bg-surface-card"
      style={wrapperStyle}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full select-none items-center gap-2 px-3.5 py-2.5 text-left hover:bg-surface-card-hover"
      >
        {leadingIcon ? (
          <span className="mr-0.5 text-text-tertiary" aria-hidden="true">
            {leadingIcon}
          </span>
        ) : null}
        <span
          className="text-2xs font-bold uppercase text-text-tertiary"
          style={{ ...titleStyle, letterSpacing: "0.11em" }}
        >
          {title}
        </span>
        {trailingBadge ? <span className="ml-2">{trailingBadge}</span> : null}
        <span
          className="ml-auto text-text-tertiary transition-transform duration-200"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
          aria-hidden="true"
        >
          {/* Right chevron — rotates 90deg on open. */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 6 6 6-6 6" />
          </svg>
        </span>
      </button>
      {open ? (
        <div className="px-3.5 pb-3 text-sm leading-7 text-text-secondary">
          {children}
        </div>
      ) : null}
    </div>
  );
}
